/*
 * This is an interim function to adjust the market data back by 1 day.
 * This adjustment is required as the Industry Analysis cron is only aware/saves the time that the job runs and not when the data analysis is relevant.
 * The result is that Industry Analysis running at UTC 4:00 is off by 1 day.
 * For example, the analysis value of the current day uses the data of the previous day.
 * Since this is a temporary solution until there are plans to re-architect Industry Analysis,
 * data adjustment will remain in this hook to allow easier refactoring in the future.
 */
export const adjustMarketTimeseriesBackOneDay = (
  marketCapTimeseries: Record<string, number> | undefined
): Record<string, number> => {
  const shiftedList = Object.entries(marketCapTimeseries || {}).map(
    ([date, value]) => {
      const shiftedDate = new Date(date);
      shiftedDate.setDate(shiftedDate.getDate() - 1);
      return [shiftedDate.toISOString().slice(0, 10), value];
    }
  );
  const adjustedTimeseries = shiftedList.reduce((timeseries, [date, value]) => {
    timeseries[date] = value;
    return timeseries;
  }, {});
  return adjustedTimeseries;
};

/*
 * This is an interim function to align the last point of market and portfolio performance by date.
 * This creates a better user experience where data is only displayed when both are available.
 * Eliminates the case when one or both data points are n/a.
 * It's not ideal to have this logic in FE and is a temporary solution until there are plans to perform this in BE.
 */
export const alignTimeseriesEndpoints = (
  portfolioTimeseries: [Date, number][],
  benchmarkTimeseries: [Date, number][]
) => {
  if (!(benchmarkTimeseries.length && portfolioTimeseries.length)) {
    return {
      portfolio: [],
      benchmark: [],
    };
  }

  let benchmarkIndex = benchmarkTimeseries?.length - 1;
  let portfolioIndex = portfolioTimeseries?.length - 1;
  let benchmarkTimestamp = benchmarkTimeseries[benchmarkIndex][0].getTime();
  let portfolioTimestamp = portfolioTimeseries[portfolioIndex][0].getTime();

  while (benchmarkTimestamp !== portfolioTimestamp) {
    if (benchmarkTimestamp > portfolioTimestamp && benchmarkIndex > 0) {
      benchmarkIndex -= 1;
      benchmarkTimestamp = benchmarkTimeseries[benchmarkIndex][0].getTime();
    } else if (portfolioTimestamp > benchmarkTimestamp && portfolioIndex > 0) {
      portfolioIndex -= 1;
      portfolioTimestamp = portfolioTimeseries[portfolioIndex][0].getTime();
    } else {
      benchmarkIndex = -1;
      portfolioIndex = -1;
      break;
    }
  }
  return {
    portfolio: portfolioTimeseries.slice(0, portfolioIndex + 1),
    benchmark: benchmarkTimeseries.slice(0, benchmarkIndex + 1),
  };
};
